// File: src/lib/graphql/client.ts
// Purpose: GraphQL Client Configuration for InstaDev Platform API
// VIBE - GraphQL Client

import { GraphQLClient } from 'graphql-request'
import { store } from '@/store'
import { logout } from '@/store/slices/authSlice'
import { toast } from 'sonner'

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT || 'https://platform.instadev.ai/graphql'

// Auth error codes that should trigger logout
const AUTH_ERROR_CODES = [
  'INVALID_TOKEN',
  'UNAUTHENTICATED',
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
  'FORBIDDEN',
]

// Auth error message patterns
const AUTH_ERROR_PATTERNS = [
  'invalid token',
  'token expired',
  'expired token',
  'invalid or expired token',
  'not authenticated',
  'unauthorized',
  'unauthenticated',
  'authentication failed',
  'jwt expired',
  'jwt malformed',
  'no token provided',
  'token not found',
]

// Function to check if message contains auth error pattern
function containsAuthErrorPattern(text: string): boolean {
  if (!text) return false
  const lowerText = text.toLowerCase()
  return AUTH_ERROR_PATTERNS.some(pattern => lowerText.includes(pattern))
}

// Function to extract errors from various graphql-request error structures
function extractGraphQLErrors(error: any): any[] {
  const errors: any[] = []

  // Try error.response.errors (standard graphql-request structure)
  if (error?.response?.errors && Array.isArray(error.response.errors)) {
    errors.push(...error.response.errors)
  }

  // Try error.response (might be the errors array directly)
  if (Array.isArray(error?.response)) {
    errors.push(...error.response)
  }

  // Try error.graphQLErrors (Apollo-style)
  if (error?.graphQLErrors && Array.isArray(error.graphQLErrors)) {
    errors.push(...error.graphQLErrors)
  }

  // Try to parse from error.response if it's a string
  if (typeof error?.response === 'string') {
    try {
      const parsed = JSON.parse(error.response)
      if (parsed.errors && Array.isArray(parsed.errors)) {
        errors.push(...parsed.errors)
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Try error.response.data.errors (nested structure)
  if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    errors.push(...error.response.data.errors)
  }

  return errors
}

// Function to check if an error is an auth error
function isAuthError(error: any): { isAuth: boolean; message: string } {
  // Debug: Log the full error structure
  console.log('[Auth Check] Full error structure:', JSON.stringify(error, null, 2))

  // Extract all possible GraphQL errors
  const graphQLErrors = extractGraphQLErrors(error)

  // Check extracted GraphQL errors
  for (const err of graphQLErrors) {
    const extensions = err.extensions || {}
    const errorCode = (extensions.code || '').toUpperCase()
    const statusCode = extensions.statusCode || extensions.http?.status
    const message = err.message || ''

    console.log('[Auth Check] Checking error:', { errorCode, statusCode, message })

    const isAuth =
      AUTH_ERROR_CODES.includes(errorCode) ||
      statusCode === 401 ||
      statusCode === 403 ||
      containsAuthErrorPattern(message)

    if (isAuth) {
      console.log('[Auth Check] Auth error detected!')
      return { isAuth: true, message: message || 'Session expired' }
    }
  }

  // Check error.message directly (ClientError message often contains the error details)
  if (error?.message && containsAuthErrorPattern(error.message)) {
    console.log('[Auth Check] Auth error in error.message!')
    return { isAuth: true, message: error.message }
  }

  // Check stringified error (sometimes error details are only in toString)
  try {
    const errorString = JSON.stringify(error)
    if (containsAuthErrorPattern(errorString)) {
      console.log('[Auth Check] Auth error in stringified error!')
      // Try to extract a better message
      const match = errorString.match(/"message"\s*:\s*"([^"]+)"/i)
      const extractedMessage = match ? match[1] : 'Session expired'
      return { isAuth: true, message: extractedMessage }
    }
  } catch {
    // Ignore stringify errors
  }

  console.log('[Auth Check] No auth error detected')
  return { isAuth: false, message: '' }
}

// Function to handle logout with cleanup
function handleAuthError(message?: string) {
  // Prevent multiple logout attempts
  const authState = store.getState().auth
  if (!authState.isAuthenticated) return

  console.warn('[GraphQL Client Auth Error]: Token invalid or expired, logging out...')

  // Show toast notification
  toast.error(message || 'Session expired. Please login again.', {
    duration: 3000,
  })

  // Dispatch logout action
  store.dispatch(logout())

  // Clear all storage
  localStorage.clear()
  sessionStorage.clear()

  // Redirect to login after a short delay to show toast
  setTimeout(() => {
    window.location.href = '/login'
  }, 500)
}

// Custom fetch wrapper that intercepts responses and checks for auth errors
const createAuthCheckingFetch = () => {
  return async (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
    const response = await fetch(url, options)

    // Clone response to read body without consuming it
    const clonedResponse = response.clone()

    try {
      const body = await clonedResponse.json()

      // Check if response contains auth errors
      if (body.errors && Array.isArray(body.errors)) {
        for (const err of body.errors) {
          const extensions = err.extensions || {}
          const errorCode = (extensions.code || '').toUpperCase()
          const statusCode = extensions.statusCode || extensions.http?.status
          const message = err.message || ''

          const isAuthErr =
            AUTH_ERROR_CODES.includes(errorCode) ||
            statusCode === 401 ||
            statusCode === 403 ||
            containsAuthErrorPattern(message)

          if (isAuthErr) {
            console.warn('[Fetch Interceptor] Auth error detected in response:', {
              code: errorCode,
              statusCode,
              message
            })
            handleAuthError(message || 'Session expired. Please login again.')
            // Still return the original response so graphql-request can handle it
            break
          }
        }
      }
    } catch {
      // Ignore JSON parse errors - response might not be JSON
    }

    return response
  }
}

class GraphQLClientSingleton {
  private static instance: GraphQLClient
  private static token: string | null = null

  static getInstance(): GraphQLClient {
    if (!this.instance) {
      this.instance = new GraphQLClient(GRAPHQL_ENDPOINT, {
        headers: {
          'Content-Type': 'application/json',
        },
        fetch: createAuthCheckingFetch(),
      })
    }
    return this.instance
  }

  static setAuthToken(token: string) {
    this.token = token
    this.instance = new GraphQLClient(GRAPHQL_ENDPOINT, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      fetch: createAuthCheckingFetch(),
    })
  }

  static clearAuthToken() {
    this.token = null
    this.instance = new GraphQLClient(GRAPHQL_ENDPOINT, {
      headers: {
        'Content-Type': 'application/json',
      },
      fetch: createAuthCheckingFetch(),
    })
  }

  static getToken(): string | null {
    return this.token
  }
}

export const graphqlClient = GraphQLClientSingleton.getInstance()
export const setAuthToken = (token: string) => GraphQLClientSingleton.setAuthToken(token)
export const clearAuthToken = () => GraphQLClientSingleton.clearAuthToken()
export const getAuthToken = () => GraphQLClientSingleton.getToken()

// Error handling wrapper - always use real API
export async function executeGraphQL<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  try {
    console.log('Making real GraphQL API call to:', GRAPHQL_ENDPOINT)
    // Ensure auth header is present from stored token when running in browser
    if (typeof window !== 'undefined') {
      try {
        const auth = JSON.parse(localStorage.getItem('persist:auth') || '{}')
        const authState = auth.auth ? JSON.parse(auth.auth) : {}
        const storedToken = authState.accessToken || localStorage.getItem('token') || null

        if (storedToken && storedToken !== GraphQLClientSingleton.getToken()) {
          console.log('Setting auth token')
          GraphQLClientSingleton.setAuthToken(storedToken)
        }
      } catch (e) {
        // ignore storage errors
      }
    }

    const client = GraphQLClientSingleton.getInstance()
    const result = await client.request<T>(query, variables)
    console.log('GraphQL API response received')
    return result
  } catch (error: any) {
    console.error('GraphQL Error:', error)

    // Debug: Log all error properties for graphql-request ClientError
    console.log('[GraphQL Error Debug] Error type:', error?.constructor?.name)
    console.log('[GraphQL Error Debug] error.response:', error?.response)
    console.log('[GraphQL Error Debug] error.response?.errors:', error?.response?.errors)
    console.log('[GraphQL Error Debug] error.response?.status:', error?.response?.status)
    console.log('[GraphQL Error Debug] error.message:', error?.message)

    // For graphql-request ClientError, errors are in response.errors
    // But we need to check multiple places due to different library versions
    const authCheck = isAuthError(error)
    if (authCheck.isAuth) {
      handleAuthError(authCheck.message)
      throw error // Still throw so calling code knows it failed
    }

    // Check if the error contains partial data
    if (error.response && error.response.data) {
      console.log('Partial data available despite error:', error.response.data)
    }

    throw error
  }
}

// Mock data responses for development
function getMockResponse<T>(query: string, variables?: Record<string, unknown>): T {
  // Check query type and return appropriate mock data
  if (query.includes('timesheets') && !query.includes('myTimesheets')) {
    // Admin timesheets query - return array of timesheet data
    return [
      {
        _id: "68c13a3cc89c81d47fade721",
        employee: {
          _id: "68c139f1c89c81d47fade647",
          firstName: "Mamta",
          lastName: "Pal",
          email: "mamta@atcuality.com"
        },
        date: "1756339200000",
        checkIn: "1756373400000",
        checkOut: "1756387800000",
        totalHours: 4,
        status: "submitted"
      },
      {
        _id: "68c13482c89c81d47fade070",
        employee: {
          _id: "68c133fec89c81d47faddeb5",
          firstName: "Amit",
          lastName: "Tiwari",
          email: "amit@atcuality.com"
        },
        date: "1756339200000",
        checkIn: "1756373400000",
        checkOut: "1756387800000",
        totalHours: 4,
        status: "submitted"
      },
      {
        _id: "68c12b6ec89c81d47fadd604",
        employee: {
          _id: "68a7e7287d3e24a6a81efa38",
          firstName: "Sachin",
          lastName: "Sagar",
          email: "sachin@atcuality.com"
        },
        date: "1756339200000",
        checkIn: "1756374000000",
        checkOut: "1756411800000",
        totalHours: 10.5,
        status: "approved"
      },
      {
        _id: "68c13c00c89c81d47fadeacd",
        employee: {
          _id: "68c13bc6c89c81d47fadea00",
          firstName: "Suraj",
          lastName: "Kumar",
          email: "suraj@atcuality.com"
        },
        date: "1756252800000",
        checkIn: "1756288800000",
        checkOut: "1756323000000",
        totalHours: 9.5,
        status: "submitted"
      },
      {
        _id: "68c13baec89c81d47fade9f7",
        employee: {
          _id: "68c13b6fc89c81d47fade903",
          firstName: "Sumit",
          lastName: "Sharma",
          email: "sumit@atcuality.com"
        },
        date: "1756252800000",
        checkIn: "1756287000000",
        checkOut: "1756323000000",
        totalHours: 10,
        status: "approved"
      },
      {
        _id: "68c13b41c89c81d47fade8fa",
        employee: {
          _id: "68c13b05c89c81d47fade81e",
          firstName: "Rahul",
          lastName: "Raj",
          email: "code@atcuality.com"
        },
        date: "1756252800000",
        checkIn: "1756290600000",
        checkOut: "1756321200000",
        totalHours: 8.5,
        status: "submitted"
      }
    ] as T
  }

  if (query.includes('employeeTimesheets')) {
    // Employee-specific timesheets with date filtering
    const employeeId = variables?.employeeId || "68c133fec89c81d47faddeb5"
    const employeeName = employeeId === "68c133fec89c81d47faddeb5" ? "Amit Tiwari" : "Employee"
    
    return [
      {
        _id: `${employeeId}_timesheet_1`,
        employee: {
          _id: employeeId,
          firstName: employeeName.split(' ')[0],
          lastName: employeeName.split(' ')[1] || '',
          email: `${employeeName.toLowerCase().replace(' ', '')}@atcuality.com`
        },
        date: "1756339200000",
        checkIn: "1756373400000",
        checkOut: "1756387800000",
        totalHours: 8,
        breakHours: 1,
        workHours: 7,
        overtimeHours: 0,
        project: {
          _id: "proj_1",
          name: "Healthcare AI Platform",
          code: "HAI"
        },
        task: {
          _id: "task_1",
          title: "Frontend Development"
        },
        description: "Working on user interface components",
        status: "approved",
        createdAt: "1756339200000",
        updatedAt: "1756339300000"
      },
      {
        _id: `${employeeId}_timesheet_2`,
        employee: {
          _id: employeeId,
          firstName: employeeName.split(' ')[0],
          lastName: employeeName.split(' ')[1] || '',
          email: `${employeeName.toLowerCase().replace(' ', '')}@atcuality.com`
        },
        date: "1756252800000",
        checkIn: "1756287000000",
        checkOut: "1756323000000",
        totalHours: 9,
        breakHours: 1,
        workHours: 8,
        overtimeHours: 1,
        project: {
          _id: "proj_2",
          name: "Patient Management System",
          code: "PMS"
        },
        task: {
          _id: "task_2",
          title: "API Integration"
        },
        description: "Integrating with external APIs",
        status: "submitted",
        createdAt: "1756252800000",
        updatedAt: "1756252900000"
      },
      {
        _id: `${employeeId}_timesheet_3`,
        employee: {
          _id: employeeId,
          firstName: employeeName.split(' ')[0],
          lastName: employeeName.split(' ')[1] || '',
          email: `${employeeName.toLowerCase().replace(' ', '')}@atcuality.com`
        },
        date: "1756166400000",
        checkIn: "1756200400000",
        checkOut: "1756232400000",
        totalHours: 8.5,
        breakHours: 0.5,
        workHours: 8,
        overtimeHours: 0.5,
        project: {
          _id: "proj_1",
          name: "Healthcare AI Platform",
          code: "HAI"
        },
        task: {
          _id: "task_3",
          title: "Database Optimization"
        },
        description: "Optimizing database queries",
        status: "approved",
        createdAt: "1756166400000",
        updatedAt: "1756166500000"
      }
    ] as T
  }

  if (query.includes('timesheetSummary')) {
    // Return employee-specific summary
    const employeeId = variables?.employeeId
    return {
      timesheetSummary: {
        totalHours: 25.5,
        workHours: 23,
        overtimeHours: 1.5,
        approvedHours: 16.5,
        pendingHours: 9,
        currentWeekHours: 8,
        currentMonthHours: 25.5,
        averageDailyHours: 8.5
      }
    } as T
  }

  if (query.includes('myTimesheets')) {
    return {
      myTimesheets: {
        edges: [
          {
            node: {
              id: '1',
              date: new Date().toISOString().split('T')[0],
              checkInTime: '09:00:00',
              checkOutTime: '17:00:00',
              totalHours: 8,
              workHours: 8,
              project: {
                name: 'Healthcare AI Platform'
              },
              status: 'APPROVED'
            },
            cursor: 'cursor1'
          },
          {
            node: {
              id: '2',
              date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
              checkInTime: '09:15:00',
              checkOutTime: '17:30:00',
              totalHours: 8.25,
              workHours: 8.25,
              project: {
                name: 'Patient Management System'
              },
              status: 'PENDING'
            },
            cursor: 'cursor2'
          }
        ],
        pageInfo: {
          hasNextPage: false,
          endCursor: 'cursor2'
        },
        totalCount: 2
      }
    } as T
  }

  if (query.includes('timesheetSummary')) {
    return {
      timesheetSummary: {
        totalHours: 40.5,
        workingDays: 5,
        averageHoursPerDay: 8.1,
        overtime: 0.5,
        approvedHours: 32.0,
        pendingHours: 8.5
      }
    } as T
  }

  // Default mock response
  return {} as T
}

// Subscription client for real-time updates
export class SubscriptionClient {
  private ws: WebSocket | null = null
  private subscriptions: Map<string, (data: unknown) => void> = new Map()
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 5

  connect(token?: string) {
    const wsUrl = GRAPHQL_ENDPOINT.replace('https://', 'wss://').replace('http://', 'ws://')

    this.ws = new WebSocket(wsUrl, 'graphql-ws')

    this.ws.onopen = () => {
      this.reconnectAttempts = 0 // Reset on successful connection

      // Send connection init
      this.ws?.send(JSON.stringify({
        type: 'connection_init',
        payload: token ? { Authorization: `Bearer ${token}` } : {}
      }))
    }

    this.ws.onmessage = (event) => {
      const message = JSON.parse(event.data)

      // Handle connection errors (including auth errors)
      if (message.type === 'connection_error') {
        const errorPayload = message.payload || {}
        const errorMessage = errorPayload.message || ''

        // Check for auth errors in connection_error
        if (
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('unauthenticated') ||
          errorPayload.code === 'INVALID_TOKEN' ||
          errorPayload.code === 'UNAUTHORIZED'
        ) {
          console.warn('[WebSocket Auth Error]: Token invalid, logging out...')
          handleAuthError(errorMessage || 'WebSocket authentication failed')
          this.disconnect()
          return
        }
      }

      // Handle errors in data messages
      if (message.type === 'error' && message.payload) {
        const errors = Array.isArray(message.payload) ? message.payload : [message.payload]
        for (const err of errors) {
          const authCheck = isAuthError({ response: { errors: [err] } })
          if (authCheck.isAuth) {
            handleAuthError(authCheck.message)
            this.disconnect()
            return
          }
        }
      }

      if (message.type === 'data' && message.id) {
        const handler = this.subscriptions.get(message.id)
        if (handler) {
          handler(message.payload)
        }
      }
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    this.ws.onclose = (event) => {
      // Don't reconnect if it was a clean close or auth error (code 4401)
      if (event.code === 4401 || event.code === 4403) {
        console.warn('[WebSocket]: Connection closed due to auth error')
        handleAuthError('WebSocket authentication failed')
        return
      }

      // Attempt reconnection with backoff, but only if authenticated
      const authState = store.getState().auth
      if (authState.isAuthenticated && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++
        const delay = Math.min(3000 * this.reconnectAttempts, 15000)
        console.log(`[WebSocket]: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
        setTimeout(() => this.connect(token), delay)
      }
    }
  }
  
  subscribe(id: string, query: string, handler: (data: unknown) => void) {
    this.subscriptions.set(id, handler)
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        id,
        type: 'start',
        payload: { query }
      }))
    }
  }
  
  unsubscribe(id: string) {
    this.subscriptions.delete(id)
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        id,
        type: 'stop'
      }))
    }
  }
  
  disconnect() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.subscriptions.clear()
  }
}

export const subscriptionClient = new SubscriptionClient()